/**
 * "Do it in winter, save 10%" — on the customer's bid page (owner, Oct 26 2026: "if clients wait till winter on a
 * service give 10% discount, so they can accept the bid and get some work done now and some in winter … make it
 * customer focused"; Oct 27: "easier for the client").
 *
 * The choice sits ON the line (WinterLinePick, drawn under each offered line by the page): "Do it now · $540" or
 * "In winter · $486". The card (WinterChoice) only explains the deal, offers "all of it in winter" and says what the
 * picks save. One state for both (useWinterChoice), held by the page.
 *
 * Every price is the server's (services/winterWork): the page only sends back which lines they picked, and the total
 * they sign is priced again on the server. Nothing is decided for them: every line starts on Now. A line whose start
 * day is already in the window shows the discount applied and no choice. The office picks which lines carry the
 * offer (the snowflake on the line in the bid editor → quote_line_items.winter_offer).
 */
import React, { useState } from 'react';
import { Snowflake, Check } from 'lucide-react';
import { Panel } from './public';

export type WinterLine = { id: number; description: string; quantity: number; now: number; winter: number; saving: number; auto: boolean; chosen: boolean };
export type WinterOffer = {
  on: boolean; pct: number; window: { from: string; to: string; words: string; nudge?: string; open?: boolean };
  auto: boolean; auto_why: string | null; lines: WinterLine[]; saving: number; saving_if_all: number; any_chosen: boolean; locked: boolean; words: string;
  booked?: { job_id: number; status: string; status_words: string; what: string; pct: number; total: number; date: string | null; window_words: string; words: string } | null;
};
export type WinterPick = { lines: number[] };

const money = (n: number) => { const v = Math.round(Number(n) * 100) / 100; return `$${v.toLocaleString('en-US', { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 })}`; };

export type WinterChoiceState = {
  offer: WinterOffer | null;
  /** The offer's line for a bid line, or null when the line carries no winter offer. */
  lineFor: (id: number) => WinterLine | null;
  /** Is the line on winter right now (picked, or already in the window)? */
  isWinter: (id: number) => boolean;
  busy: boolean;
  setLine: (id: number, winter: boolean) => void;
  setAll: (winter: boolean) => void;
  savingNow: number;
  choosable: WinterLine[];
  allIn: boolean;
};

/** The picks, shared by the card and the pills on the lines. `offer` null (nothing offered, or signed) → an idle state. */
export function useWinterChoice(offer: WinterOffer | null, {
  onChange, price,
}: {
  /** Their picks, and the server-priced saving for them (0 until the server answers). */
  onChange: (pick: WinterPick, saving: number) => void;
  /** Ask the server what the picks come to — never worked out here. */
  price: (pick: WinterPick) => Promise<{ saving: number; lines: WinterLine[] } | null>;
}): WinterChoiceState {
  const [picked, setPicked] = useState<Set<number>>(() => new Set((offer?.lines || []).filter(l => l.chosen).map(l => l.id)));
  const [busy, setBusy] = useState(false);
  const [serverLines, setServerLines] = useState<WinterLine[] | null>(null);
  const lines = serverLines || offer?.lines || [];
  const choosable = (offer?.lines || []).filter(l => !l.auto);
  const allIn = choosable.length > 0 && choosable.every(l => picked.has(l.id));

  async function apply(next: Set<number>) {
    if (!offer) return;
    for (const l of offer.lines) if (l.auto) next.add(l.id);
    setPicked(next);
    const pick = { lines: Array.from(next) };
    setBusy(true);
    try {
      const priced = await price(pick);
      if (priced) { setServerLines(priced.lines); onChange(pick, Number(priced.saving) || 0); }
      else onChange(pick, 0);
    } catch { onChange(pick, 0); }
    finally { setBusy(false); }
  }
  const lineFor = (id: number) => lines.find(l => l.id === id) || null;
  const isWinter = (id: number) => { const l = lineFor(id); return !!l && (l.auto || picked.has(id)); };
  return {
    offer, lineFor, isWinter, busy,
    setLine: (id, winter) => { if (!lineFor(id) || picked.has(id) === winter) return; const n = new Set(picked); if (winter) n.add(id); else n.delete(id); apply(n); },
    setAll: winter => apply(winter ? new Set((offer?.lines || []).map(l => l.id)) : new Set<number>()),
    savingNow: lines.filter(l => l.auto || picked.has(l.id)).reduce((t, l) => t + l.saving, 0),
    choosable, allIn,
  };
}

/** The Now / In winter choice under one line. Both prices, the saving; a line already in the window says so instead. */
export function WinterLinePick({ state, id }: { state: WinterChoiceState; id: number }) {
  const l = state.lineFor(id);
  const offer = state.offer;
  if (!l || !offer) return null;
  const winter = state.isWinter(id);
  if (l.auto) return <p className="mt-2 text-[14px] font-semibold text-sky-800"><Snowflake className="inline h-4 w-4 mr-1 -mt-0.5" aria-hidden />In our winter window — {offer.pct}% off applied: {money(l.winter)}.</p>;
  return (
    <div className="mt-2 grid grid-cols-2 gap-2" role="radiogroup" aria-label={`When to do: ${l.description}`}>
      <button type="button" role="radio" aria-checked={!winter} disabled={state.busy} onClick={() => state.setLine(id, false)}
        className={`press min-h-[48px] rounded-2xl border-2 text-[14px] font-bold px-2 ${!winter ? 'border-brand-600 bg-brand-100 text-gray-900' : 'border-brand-200 bg-white text-gray-700'}`}>
        Do it now · {money(l.now)}
      </button>
      <button type="button" role="radio" aria-checked={winter} disabled={state.busy} onClick={() => state.setLine(id, true)}
        className={`press min-h-[48px] rounded-2xl border-2 text-[14px] font-bold px-2 ${winter ? 'border-sky-600 bg-sky-100 text-sky-900' : 'border-brand-200 bg-white text-gray-700'}`}>
        <Snowflake className="inline h-4 w-4 mr-1 -mt-0.5" aria-hidden />In winter · {money(l.winter)}
        <span className="block text-[12px] font-semibold">{offer.pct}% off, saves {money(l.saving)}</span>
      </button>
    </div>
  );
}

/**
 * The card: what the deal is, "all of it in winter", what their picks save, and — once anything is picked — the bill
 * split in two: what is due for the work done now and what is due for the winter work (owner, Oct 29 2026: Chris Land
 * read two totals on two bids and could not tell which was which). `split` is the page's own arithmetic on the server's
 * prices: now = the total with the winter lines taken out, winter = the winter lines at their winter price.
 * Signed: what was agreed.
 */
export default function WinterChoice({ offer, state, split = null }: { offer: WinterOffer; state: WinterChoiceState; split?: { now: number; winter: number } | null }) {
  // Signed: say what was agreed, no choice.
  if (offer.locked) {
    const winterLines = offer.lines.filter(l => l.chosen);
    if (!winterLines.length && !offer.booked) return null;
    return (
      <Panel tone="brand">
        <p className="flex items-center gap-2 text-[15px] font-black uppercase tracking-tight text-gray-900"><Snowflake className="h-5 w-5 text-sky-600" aria-hidden /> Winter work</p>
        <p className="text-[15px] text-gray-900 mt-2">{offer.booked?.words || `${winterLines.map(l => l.description).join(', ')} — ${offer.pct}% off, done between ${offer.window.words} on a day we agree, weather permitting.`}</p>
      </Panel>
    );
  }
  const { choosable, allIn, savingNow, busy } = state;
  return (
    <Panel>
      <p className="flex items-center gap-2 text-[15px] font-black uppercase tracking-tight text-gray-900"><Snowflake className="h-5 w-5 text-sky-600" aria-hidden /> Do it in winter, save {offer.pct}%</p>
      <p className="text-[15px] text-gray-800 mt-2 leading-relaxed">{offer.words}</p>
      {offer.auto && offer.auto_why && (
        <p className="mt-3 rounded-2xl bg-sky-50 border border-sky-200 px-4 py-3 text-[15px] text-sky-900"><Check className="inline h-4 w-4 mr-1 -mt-0.5" aria-hidden />{offer.auto_why}</p>
      )}
      {choosable.length > 1 && (
        <button type="button" disabled={busy} onClick={() => state.setAll(!allIn)}
          className="press mt-3 text-[14px] font-bold text-brand-700 underline min-h-[48px]">
          {allIn ? 'Do it all now instead' : `All of it in winter — save ${money(offer.saving_if_all)}`}
        </button>
      )}
      <p className="mt-2 text-[14px] text-gray-700">
        {savingNow > 0
          ? <>Winter work saves you <b>{money(savingNow)}</b>. It is done between {offer.window.words}, on a day we agree with you — we text you in late November to pick the week. The rest is done as quoted.</>
          : <>Everything is done as quoted. Each line below with a <Snowflake className="inline h-3.5 w-3.5 -mt-0.5 text-sky-600" aria-hidden /> has a winter price — tap it and the total at the bottom drops.</>}
      </p>
      {savingNow > 0 && split && split.winter > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-2 text-center">
          <div className="rounded-2xl bg-gray-50 border border-gray-200 px-3 py-2">
            <p className="text-[11px] uppercase tracking-widest text-gray-600">Now</p>
            <p className="text-lg font-black text-gray-900 tabular-nums">{money(Math.max(0, split.now))}</p>
            <p className="text-[12px] text-gray-600">billed when that work is done</p>
          </div>
          <div className="rounded-2xl bg-sky-50 border border-sky-200 px-3 py-2">
            <p className="text-[11px] uppercase tracking-widest text-sky-800">In winter</p>
            <p className="text-lg font-black text-sky-900 tabular-nums">{money(split.winter)}</p>
            <p className="text-[12px] text-sky-800">{offer.pct}% off, billed after the winter visit</p>
          </div>
        </div>
      )}
    </Panel>
  );
}
